/**
 * Incremental line-oriented SSE parser for the agentic chat stream.
 *
 * The opted-in ("Vercel UI message stream") wire interleaves two frame shapes:
 *   - legacy frames:  `data: {"event": ...}\n\n` (no id)
 *   - Vercel frames:  `id: N\ndata: {"type": ...}\n\n`
 * Older servers that ignore the opt-in header emit legacy frames separated by a
 * single `\n` instead of a blank line, so this parser dispatches on each
 * complete `data:` line rather than waiting for blank-line event boundaries.
 * An `id:` line applies to the next `data:` line only; a blank line clears any
 * pending id, matching the SSE spec closely enough for both wire shapes.
 */
export interface SseFrame {
  /** SSE event id (Vercel frames), or null for id-less legacy frames. */
  id: string | null;
  /** Raw payload of the `data:` line (JSON text or the literal `[DONE]`). */
  data: string;
}

export class SseLineParser {
  private buffer = "";
  private pendingId: string | null = null;

  push(text: string): SseFrame[] {
    this.buffer += text;
    const frames: SseFrame[] = [];

    let newlineIndex = this.buffer.indexOf("\n");
    while (newlineIndex !== -1) {
      const line = this.buffer.slice(0, newlineIndex);
      this.buffer = this.buffer.slice(newlineIndex + 1);
      const frame = this.consumeLine(line);
      if (frame) {
        frames.push(frame);
      }
      newlineIndex = this.buffer.indexOf("\n");
    }

    return frames;
  }

  /** Flush a trailing unterminated line (stream ended without a final newline). */
  end(): SseFrame[] {
    if (!this.buffer) {
      return [];
    }
    const line = this.buffer;
    this.buffer = "";
    const frame = this.consumeLine(line);
    return frame ? [frame] : [];
  }

  private consumeLine(rawLine: string): SseFrame | null {
    const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;

    if (line === "") {
      this.pendingId = null;
      return null;
    }

    if (line.startsWith(":")) {
      return null;
    }

    if (line.startsWith("id:")) {
      this.pendingId = line.slice(3).trim();
      return null;
    }

    if (line.startsWith("data:")) {
      const id = this.pendingId;
      this.pendingId = null;
      let data = line.slice(5);
      if (data.startsWith(" ")) {
        data = data.slice(1);
      }
      return { id, data };
    }

    return null;
  }
}
